/**
 * Splits running text into one sentence per line without breaking after common
 * Polish abbreviations ("np. tak" must stay on one line).
 *
 * This used to be a single regex with a negative lookbehind built at render
 * time. JavaScriptCore only gained lookbehind support in Safari 16.4, so on
 * iOS 16.3 and earlier `new RegExp` threw a SyntaxError while rendering the
 * text screen - taking the whole app down for anyone who had the
 * "each sentence on its own line" setting switched on.
 *
 * The replacement walks the periods instead and looks backwards at the token
 * that precedes each one, which needs no lookbehind and no `u` flag.
 */

/** Abbreviations that should not end a sentence. Compared case-insensitively. */
export const POLISH_ABBREVIATIONS = [
  'ul', 'al', 'pl', // streets: ulica, aleja, plac
  'np', 'itd', 'itp', 'tj', 'tzn', 'tzw', // e.g., etc., i.e.
  'dr', 'mgr', 'inż', 'prof', 'doc', // titles
  'św', 'śp', // saint, late
  'm.in', 'min', 'maks', 'max', // among others, min, max
  'wg', 'ok', 'ca', // according to, approximately
  'godz', 'sek', 's', // time units
  'tel', 'fax', 'e-mail', // contact
  'str', 'pkt', 'ust', 'art', 'par', // page, point, article, paragraph
  'r', 'w', // rok (year), wiek (century)
  'nr', 'lp', // number, ordinal
  'zł', 'gr', 'pln', 'eur', 'usd', // currencies
  'kg', 'g', 'mg', 'l', 'ml', 'km', 'm', 'cm', 'mm', // units
  'proc', '%', // percent
  'sp', 'z.o.o', 's.a', 's.c', // company types
  'im', 'gen', 'ppłk', 'płk', 'mjr', 'kpt', // names, military ranks
  'p', 'pp', // page(s)
  'jw', 'cd', 'cdn', // as above, continued
  'ang', 'niem', 'fr', 'łac', // languages
  'obs', 'przyp', 'red', // notes
  'dz', 'cyt', 'op', // works
  'pn', 'wt', 'śr', 'cz', 'pt', 'sb', 'nd', // weekdays
  'sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru', // months
] as const

const ABBREVIATIONS = new Set<string>(POLISH_ABBREVIATIONS.map((abbrev) => abbrev.toLowerCase()))

/**
 * Leading characters that are punctuation rather than part of the abbreviation,
 * e.g. the opening bracket in "(np." or the quote in "„np.".
 *
 * The kept set is letters (including Polish diacritics, which all sit inside
 * U+00C0-U+024F), digits, and the three symbols that appear inside
 * abbreviations themselves: '.', '-' and '%'.
 */
const LEADING_PUNCTUATION = /^[^0-9A-Za-zÀ-ɏ.%-]+/
const WHITESPACE = /\s/
const PERIOD_BEFORE_WHITESPACE = /\.\s+/g

/**
 * True when the period at `periodIndex` closes a known abbreviation rather than a
 * sentence.
 *
 * Exported because the speech chunker in text-util.ts needs exactly the same
 * judgement: it used to split on every `.`, so "np. tak" became two utterances
 * and the highlight unit was cut mid-phrase.
 */
export function isAbbreviationPeriod(text: string, periodIndex: number): boolean {
  return endsWithKnownAbbreviation(text, periodIndex)
}

function endsWithKnownAbbreviation(text: string, periodIndex: number): boolean {
  let start = periodIndex
  while (start > 0 && !WHITESPACE.test(text[start - 1])) {
    start -= 1
  }

  const token = text.slice(start, periodIndex).replace(LEADING_PUNCTUATION, '')
  return token.length > 0 && ABBREVIATIONS.has(token.toLowerCase())
}

/**
 * Replaces ". " with ".\n", except where the period belongs to an abbreviation.
 * The whitespace run after the period is consumed, matching the previous behaviour.
 */
export function breakSentencesAtAbbreviationSafePeriods(text: string): string {
  if (!text) {
    return text
  }

  let result = ''
  let cursor = 0

  PERIOD_BEFORE_WHITESPACE.lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = PERIOD_BEFORE_WHITESPACE.exec(text)) !== null) {
    if (endsWithKnownAbbreviation(text, match.index)) {
      continue
    }

    result += text.slice(cursor, match.index) + '.\n'
    cursor = match.index + match[0].length
  }

  return result + text.slice(cursor)
}
